import Head from 'expo-router/head';
import { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import PSLinkButton from '@/components/ui/PSLinkButton';

/**
 * ── /components/ps-link-button ───────────────────────────────────────────
 *
 * PSLinkButton's real surface is small on purpose (see the component's own
 * docstring: a `Text` with `onPress`, not a `Button` wrapping one) — `title`,
 * `onPress`, `disabled`, and a two-way `variant`. There's no `loading`, no
 * `icon`, no `children`: the atom owns brand/underline/size so every link in
 * the app reads the same. What's worth previewing is less "does this prop
 * work" and more HOW it's actually composed at real call sites — as a
 * standalone CTA, disabled through a cooldown, and nested INSIDE a sentence
 * (RN renders a nested pressable `Text` inline, which is the whole reason
 * this atom is `Text`-based rather than a boxed button).
 * ─────────────────────────────────────────────────────────────────────────
 */

// Preview-only stand-in for the resend cooldown: the real
// `useResendCountdown` hook runs a 20s timer anchored to a timestamp in the
// auth draft (see src/hooks/useResendCountdown.ts) so it survives a
// back-navigation unmount/remount. That's not reproducible from a static
// preview page, so this is a plain local state machine over the same THREE
// states the real one drives PSLinkButton through — shortened to 5s so the
// demo doesn't make you wait.
const DEMO_COOLDOWN_SECONDS = 5;

export default function PSLinkButtonPreview() {
  // Drives the live resend demo below — idle → sending → cooldown → idle,
  // the same cycle verify-mobile.tsx / verify-email.tsx run for real. `status`
  // is DERIVED from `sending` + `remaining` rather than stored as its own
  // state (mirrors useResendCountdown's own "derive, don't stamp" comment) —
  // that keeps the effect below to a single setState (decrementing the
  // clock), never one that reacts to another state's value by setting a
  // third, which is what trips react-hooks/set-state-in-effect.
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const status = sending ? 'sending' : remaining > 0 ? 'cooldown' : 'idle';

  useEffect(() => {
    if (remaining <= 0) return;
    const id = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining]);

  const handleResend = () => {
    setSending(true);
    // Stand-in for the real `authApi.resendOtp` round-trip.
    setTimeout(() => {
      setSending(false);
      setRemaining(DEMO_COOLDOWN_SECONDS);
    }, 1200);
  };

  return (
    <>
      <Head>
        <title>PSLinkButton · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* variant — the only visual axis besides disabled. `brand` (default,
            $brand purple) is the common case: venue-address links repeated
            verbatim across bookings/[id], requests/[id], and
            events/[id]/confirm ("Open in google maps"). `neutral` ($textDark,
            same underline) is for a link sitting next to other dark text where
            brand-purple would compete for attention — profile's "Edit" beside
            the "Your Details" heading, and EventDetailMeta's venue-name link
            next to a location pin icon. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              variant — brand (default, $brand purple): venue links in
              bookings/[id], requests/[id], events/[id]/confirm. neutral
              ($textDark): profile&apos;s &quot;Edit&quot;,
              EventDetailMeta&apos;s venue name
            </Text>
            <XStack gap="$xl" items="center">
              <PSLinkButton title="Open in google maps" onPress={() => {}} />
              <PSLinkButton title="Edit" variant="neutral" onPress={() => {}} />
            </XStack>
          </YStack>

          {/* idle vs disabled — dims to 0.4 rather than going gray (see the
            component's docstring: it stays recognizably the same brand link,
            just inert) and drops both the tap handler and the pointer cursor,
            unlike PSButton's disabled which just ignores taps while keeping
            the handler wired. Real disabled call site: the auth OTP screens'
            resend link during its cooldown window (see the live demo below
            for the full state machine this feeds into). */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              idle vs disabled — 0.4 opacity, no press, no pointer cursor
            </Text>
            <XStack gap="$xl" items="center">
              <PSLinkButton title="Resend" onPress={() => {}} />
              <PSLinkButton title="Resend" onPress={() => {}} disabled />
            </XStack>
          </YStack>

          {/* live — the actual three-state cycle verify-mobile.tsx / verify-
            email.tsx drive this exact PSLinkButton through: idle ("Resend") →
            tap → sending ("Sending…", disabled while the request is in
            flight) → cooldown ("Resend (in N seconds)", disabled, ticking
            down) → idle again. Both real screens compute this same title
            string inline from `resending`/`canResend`/`remaining` — PSLinkButton
            itself has no notion of a countdown, it just renders whatever
            title and disabled it's handed. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap to fire a fake resend; cycles idle → sending →
              {DEMO_COOLDOWN_SECONDS}s cooldown → idle, same as the OTP
              screens&apos; real resend link
            </Text>
            <XStack items="baseline" gap="$xs">
              <Text color="$textDarkMuted">Didn&apos;t get an otp?</Text>
              <PSLinkButton
                title={
                  status === 'sending'
                    ? 'Sending…'
                    : status === 'cooldown'
                      ? `Resend (in ${remaining} seconds)`
                      : 'Resend'
                }
                disabled={status !== 'idle'}
                onPress={handleResend}
              />
            </XStack>
          </YStack>

          {/* nested inline in a sentence — the app's recurring pattern for a
            link that reads as PART of a paragraph rather than a control
            beside it: the support-contact phone/email in
            bookings/[id]/requests/[id] ("reach out to them on {phone} or
            {email}"), PSHomeSectionEmpty's "Refresh", PSHomeSectionError's
            "Retry", and UserDetails' "complete your profile" /
            "verify your email" nudges. This works because RN lays a nested
            pressable `Text` out INLINE inside its parent `Text` — exactly why
            PSLinkButton is a bare `Text` and not a `Button` wrapping one; a
            boxed button would break onto its own line here. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              nested inline in a sentence — support contact lines, empty/error
              states, profile nudges
            </Text>
            <Text
              fontFamily="$body"
              fontSize="$base"
              color="$textDarkMuted"
              maxWidth={320}
            >
              <Text fontWeight="$semibold" color="$textDark">
                Alex
              </Text>{' '}
              will be your go-to person for any queries related to the event.
              You can reach out to them on{' '}
              <PSLinkButton title="+91 98765 43210" onPress={() => {}} /> or{' '}
              <PSLinkButton title="alex@puttingscene.in" onPress={() => {}} />.
            </Text>
          </YStack>

          {/* standalone under error/empty copy — PSFetchError, EventErrorPage,
            and delete-account's upcoming-bookings block all end this way: a
            line of copy explaining what went wrong, then this as the one way
            out, centered underneath. Shown here the same way those three
            compose it. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              standalone under error copy — PSFetchError, EventErrorPage,
              delete-account&apos;s failed-fetch block
            </Text>
            <YStack
              bg="$color.background"
              p="$space.xl"
              gap="$sm"
              items="center"
              width={280}
            >
              <Text
                fontFamily="$body"
                fontSize="$small"
                color="$textDarkMuted"
                textAlign="center"
              >
                We couldn&apos;t check whether you have bookings coming up.
              </Text>
              <PSLinkButton title="Try again" onPress={() => {}} />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
